import {
  FluidAlignment,
  FluidButtonType,
  FluidColor,
  FluidFileItem,
  FluidFileListHeaderConfiguration,
  FluidFileUploadActionsConfig,
  FluidFileUploadButtonConfig,
  FluidIconConfig,
  FluidIconKey,
  FluidKeyedObject,
  FluidSize,
  FluidTableHeader,
} from '@lmig/fluid-core';
import { FluidTableTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-table-translation-options.type';
import { h } from '@stencil/core';

import {
  FILE_UPLOAD_VALID_DATAPATHS,
  INVALID_FILE_UPLOAD_COLUMN,
} from '../../../constants/constants';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../../utils/utils';

// ================================================================ //
// -- Functional Components (exclusive to FLUID File Upload )
//

/**
 * Creates the file upload area. Responsible for rendering the upload area container,
 * and deciding which text component to display (FileInformation or UploadText) based on the
 * value of acceptMultiple and files array length
 * @param props
 * @constructor
 */

export interface FluidFileUploadEmptyConfig {
  message: string;
  linkText: string;
}

export interface FluidFileUploadPopulatedConfig {
  message: (files: File[]) => string;
  linkText: string;
  linkBrowse: string;
}

interface FluidTableHeaderTranslationOptions {
  label?: string;
  helpText?: string;
}

export interface FluidFileUploadTranslationOptions {
  emptyStateConfig?: FluidFileUploadEmptyConfig;
  populatedStateConfig?: FluidFileUploadPopulatedConfig;
  supplementaryInformation?: string;
  fileListHeaderConfig?: {
    name?: FluidTableHeaderTranslationOptions;
    size?: FluidTableHeaderTranslationOptions;
    lastModified?: FluidTableHeaderTranslationOptions;
    type?: FluidTableHeaderTranslationOptions;
  };
  uploadControlConfig: {
    primaryButtonConfig?: {
      label: string;
    };
    secondaryButtonConfig?: {
      label: string;
    };
    [eventKey: string]: {
      label: string;
    };
  };
  fileListTable?: FluidTableTranslationOptions;
}

type FileUploadAreaProps = {
  onClicked: Function;
  files: File[];
  onClear: Function;
  supplementaryInformation: string;
  dragHandlers: FluidKeyedObject<Function>;
  emptyStateConfig: FluidFileUploadEmptyConfig;
  populatedStateConfig: FluidFileUploadPopulatedConfig;
  uploadError?: string;
  fluidTranslate?: FluidFileUploadTranslationOptions;
  i18nStore?: FluidI18nStore;
  validateFile: (file: File) => boolean;
  acceptMultiple: boolean;
  size?: FluidSize;
};

export const FileUploadArea = (props: FileUploadAreaProps) => {
  const {
    onClicked,
    files,
    onClear,
    supplementaryInformation,
    dragHandlers,
    emptyStateConfig,
    populatedStateConfig,
    uploadError,
    fluidTranslate,
    i18nStore,
    acceptMultiple,
    size,
  } = props;

  return (
    <div class={'file-upload-area'} {...dragHandlers}>
      {files.length > 0 ? (
        <FileInformation
          populatedStateConfig={populatedStateConfig}
          onClear={onClear}
          onClicked={onClicked}
          files={files}
          uploadError={uploadError}
          fluidTranslate={fluidTranslate}
          i18nStore={i18nStore}
          acceptMultiple={acceptMultiple}
          size={size}
        />
      ) : (
        <UploadText
          onClicked={onClicked}
          uploadError={uploadError}
          supplementaryInformation={supplementaryInformation}
          emptyStateConfig={emptyStateConfig}
          fluidTranslate={fluidTranslate}
          i18nStore={i18nStore}
          size={size}
        />
      )}
    </div>
  );
};

/**
 * Renders the file information as text, with appropriate icon and clear text.
 * @param props
 * @constructor
 */
export type FileInformationProps = {
  onClear: Function;
  onClicked: Function;
  files: File[];
  uploadError?: string;
  populatedStateConfig: FluidFileUploadPopulatedConfig;
  fluidTranslate?: FluidFileUploadTranslationOptions;
  i18nStore?: FluidI18nStore;
  acceptMultiple: boolean;
  size?: FluidSize;
};
export const FileInformation = (props: FileInformationProps) => {
  const {
    onClear,
    onClicked,
    files,
    uploadError,
    populatedStateConfig,
    fluidTranslate,
    i18nStore,
    acceptMultiple,
    size,
  } = props;
  return (
    <div
      class={{
        wrapper: true,
        [`wrapper-${size}`]: !!size,
      }}>
      <UploadIcon uploadError={uploadError} isUpload={false} size={size} />
      {i18nStore?.translate(
        fluidTranslate?.populatedStateConfig?.message as unknown as string,
        { files }
      ) || populatedStateConfig.message(files)}{' '}
      {acceptMultiple ? (
        <fluid-link onClick={() => onClicked()} inline={true}>
          <span class={'link-text'}>
            {i18nStore?.translate(
              fluidTranslate?.populatedStateConfig?.linkBrowse
            ) || populatedStateConfig.linkBrowse}
          </span>
        </fluid-link>
      ) : (
        <fluid-link onClick={() => onClear()} inline={true}>
          <span class={'link-text'}>
            {i18nStore?.translate(
              fluidTranslate?.populatedStateConfig?.linkText
            ) || populatedStateConfig.linkText}
          </span>
        </fluid-link>
      )}
    </div>
  );
};

const UploadIcon = (props: {
  isUpload?: boolean;
  uploadError?: string;
  size?: FluidSize;
}) => {
  const { uploadError, isUpload, size } = props;

  let iconSize: FluidSize;

  switch (size) {
    case FluidSize.LARGE:
      iconSize = FluidSize.LARGE;
      break;
    case FluidSize.EXTRA_LARGE:
      iconSize = FluidSize.EXTRA_LARGE;
      break;
    default:
      iconSize = FluidSize.MEDIUM; // Default size if none is provided
  }

  const fileIcon: FluidIconConfig = {
    key: FluidIconKey.FILE_ALT,
    size: iconSize,
    popoverConfig: undefined,
    color: undefined,
  };

  const uploadIcon: FluidIconConfig = {
    key: FluidIconKey.UPLOAD,
    size: iconSize,
    popoverConfig: undefined,
    color: undefined,
  };

  const errorIcon: FluidIconConfig = {
    popoverConfig: {
      message: uploadError,
      persistable: false,
    },
    key: FluidIconKey.WARNING,
    color: FluidColor.WARNING,
  };

  return (
    <fluid-icon
      config={uploadError ? errorIcon : isUpload ? uploadIcon : fileIcon}
    />
  );
};

/**
 * Renders the dropzone text with upload icon and click/drop handler.
 * @param props
 * @constructor
 */
type UploadTextProps = {
  onClicked: Function;
  supplementaryInformation: string;
  emptyStateConfig: { message: string; linkText: string };
  uploadError?: string;
  fluidTranslate?: FluidFileUploadTranslationOptions;
  i18nStore?: FluidI18nStore;
  size?: FluidSize;
};
export const UploadText = (props: UploadTextProps) => {
  const {
    onClicked,
    supplementaryInformation,
    uploadError,
    emptyStateConfig,
    fluidTranslate,
    i18nStore,
    size,
  } = props;

  return (
    <div
      class={{
        wrapper: true,
        [`wrapper-${size}`]: !!size,
      }}>
      <UploadIcon uploadError={uploadError} isUpload={true} size={size} />
      <span>
        {i18nStore?.translate(fluidTranslate?.emptyStateConfig?.message) ||
          emptyStateConfig.message}
      </span>
      <fluid-link onClick={() => onClicked()} inline={true}>
        <span class={'link-text'}>
          {i18nStore?.translate(fluidTranslate?.emptyStateConfig?.linkText) ||
            emptyStateConfig.linkText}
        </span>
      </fluid-link>
      {(!!supplementaryInformation ||
        fluidTranslate?.supplementaryInformation) && (
        <span>
          {i18nStore?.translate(fluidTranslate?.supplementaryInformation) ||
            supplementaryInformation}
        </span>
      )}
    </div>
  );
};

/**
 * Renders the filelist table
 * @param props
 * @constructor
 */
export const FileList = (props: {
  files: File[];
  fileListUpdated: Function;
  fileListHeaderConfig?: FluidFileListHeaderConfiguration;
  fluidTranslate?: FluidFileUploadTranslationOptions;
}) => {
  const { files, fileListUpdated, fileListHeaderConfig, fluidTranslate } =
    props;

  if (fileListHeaderConfig?.customHeaderSet) {
    fileListHeaderConfig.customHeaderSet = validateCustomFileUploadHeaders(
      fileListHeaderConfig?.customHeaderSet
    );
  }

  const createHeader = (label, dataPath) => ({
    label,
    dataPath,
    fluidTranslate: fluidTranslate?.fileListHeaderConfig?.[dataPath],
    ...fileListHeaderConfig?.[dataPath],
  });

  const headers = fileListHeaderConfig?.customHeaderSet
    ? fileListHeaderConfig?.customHeaderSet
    : [
        createHeader('Filename', 'name'),
        createHeader('Type', 'type'),
        createHeader('Filesize', 'size'),
        createHeader('Last Modified', 'lastModified'),
      ];

  const dataItems: FluidFileItem[] = files.map((file) => ({
    name: file.name,
    fileSelected: true,
    type: file.type,
    size: file.size,
    lastModified: new Date(file.lastModified).toLocaleDateString(),
    file,
  }));

  return (
    files.length > 0 && (
      <fluid-table
        tableId={'upload-table'}
        paginated={false}
        columnAdjustments={fileListHeaderConfig?.columnAdjustments}
        handleDelete={true}
        fluidTranslate={fluidTranslate?.fileListTable}
        enableDeleteAll={true}
        onDataMutated={(data) =>
          fileListUpdated(
            (data.detail.updatedDataSet as FluidFileItem[]).map(
              (f: FluidFileItem) => f.file
            )
          )
        }
        dataItems={dataItems}
        headers={headers}
      />
    )
  );
};

/**
 * Creates the upload action grid.
 * @param props
 * @constructor
 */
export const FileUploadActions = (props: {
  onClear: Function;
  onUpload: Function;
  onActionClick: Function;
  actions: FluidFileUploadActionsConfig;
  fluidTranslate?: FluidFileUploadTranslationOptions;
  i18nStore?: FluidI18nStore;
  actionsDisabled?: boolean;
  files: File[];
}) => {
  const {
    onClear,
    onUpload,
    onActionClick,
    actions,
    fluidTranslate,
    i18nStore,
    actionsDisabled,
    files,
  } = props;
  return (
    <fluid-grid>
      <fluid-grid-item
        colSm={12}
        alignItems={actions.actionsPlacement || FluidAlignment.LEFT}>
        <UploadActionSet
          actionsDisabled={actionsDisabled}
          files={files}
          actions={actions}
          onActionClick={onActionClick}
          onClear={onClear}
          onUpload={onUpload}
          fluidTranslate={fluidTranslate}
          i18nStore={i18nStore}
        />
      </fluid-grid-item>
    </fluid-grid>
  );
};

/**
 * Creates and returns an array of actions for the upload controls. If the placement is right aligned, order is reversed to keep
 * primary button right aligned.
 * @param props
 * @constructor
 */
export const UploadActionSet = (props: {
  onClear: Function;
  onUpload: Function;
  onActionClick: Function;
  actions: FluidFileUploadActionsConfig;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidFileUploadTranslationOptions;
  actionsDisabled?: boolean;
  files: File[];
}) => {
  const {
    onClear,
    onUpload,
    onActionClick,
    actions,
    i18nStore,
    fluidTranslate,
    actionsDisabled,
    files,
  } = props;
  const { additionalActions, primaryButtonConfig, secondaryButtonConfig } =
    actions;

  const enabledByFileList = (enabler: (files: File[]) => boolean | undefined) =>
    Utils.isFunction(enabler) && enabler(files);
  const actionElements = [
    <fluid-button
      type={FluidButtonType.PRIMARY}
      id={primaryButtonConfig?.identifier || 'upload-primary-action'}
      label={
        i18nStore?.translate(
          fluidTranslate?.uploadControlConfig?.primaryButtonConfig?.label
        ) ||
        primaryButtonConfig?.label ||
        'Upload Files'
      }
      onClick={() => onUpload()}
      removeHorizontalMargin={actions.actionsPlacement === FluidAlignment.RIGHT}
      disabled={
        primaryButtonConfig?.disabled ||
        (actionsDisabled && !enabledByFileList(primaryButtonConfig?.enabledIf))
      }
    />,
    <fluid-button
      type={FluidButtonType.SECONDARY}
      id={secondaryButtonConfig?.identifier || 'upload-secondary-action'}
      label={
        i18nStore?.translate(
          fluidTranslate?.uploadControlConfig?.secondaryButtonConfig?.label
        ) ||
        secondaryButtonConfig?.label ||
        'Clear'
      }
      onClick={() => onClear()}
      disabled={
        secondaryButtonConfig?.disabled ||
        (actionsDisabled &&
          !enabledByFileList(secondaryButtonConfig?.enabledIf))
      }
    />,
    ...(additionalActions || []).map(
      (action: FluidFileUploadButtonConfig, idx: number) => {
        return (
          <fluid-button
            type={FluidButtonType.TERTIARY}
            id={action.identifier || `upload-additional-action-${idx}`}
            label={
              i18nStore?.translate(
                fluidTranslate?.uploadControlConfig?.[action.eventKey]?.label
              ) || action.label
            }
            onClick={() => onActionClick(action.eventKey)}
            disabled={
              action.disabled ||
              (actionsDisabled && !enabledByFileList(action?.enabledIf))
            }
          />
        );
      }
    ),
  ];
  return actions.actionsPlacement === FluidAlignment.RIGHT
    ? actionElements.reverse()
    : actionElements;
};

// ================================================================ //
// -- Functional Component Utilities (exclusive to FLUID File Upload )
//

const validateCustomFileUploadHeaders = (
  customHeaders: FluidTableHeader[]
): FluidTableHeader[] => {
  return customHeaders.reduce(
    (validHeaders: FluidTableHeader[], current: FluidTableHeader) => {
      if (
        current.dataPath &&
        FILE_UPLOAD_VALID_DATAPATHS.indexOf(current.dataPath) === -1
      ) {
        Utils.consoleWarn(
          { tagName: 'fluid-file-upload' } as HTMLElement,
          INVALID_FILE_UPLOAD_COLUMN(current.dataPath)
        );
      } else {
        validHeaders.push(current);
      }
      return validHeaders;
    },
    []
  );
};
